import { useMemo } from "react";

export type TenantKey = "beapolo" | "intelligence";

interface OpenGraphConfig {
  title?: string;
  description?: string;
  image?: string;
}

interface TenantBranding {
  key: TenantKey;
  displayName: string;
  title: string;
  hostnameIncludes?: string[];
  assets: {
    favicon: string;
    logo: string;
    logoWhite?: string;
  };
  openGraph?: OpenGraphConfig;
}

const TENANT_CONFIG: Record<TenantKey, TenantBranding> = {
  beapolo: {
    key: "beapolo",
    displayName: "BeApolo",
    title: "BeApolo",
    assets: {
      favicon: "/beapolo/favicon.ico",
      logo: "/beapolo/logo.svg",
      logoWhite: "/beapolo/logo-white.svg",
    },
    openGraph: {
      title: "BeApolo",
      description: "BeApolo — intelligence and automation for media teams.",
      image: "/beapolo/og.png",
    },
  },
  intelligence: {
    key: "intelligence",
    displayName: "Intelligence",
    title: "Intelligence Platform",
    hostnameIncludes: ["intelligence"],
    assets: {
      favicon: "/intelligence/favicon.ico",
      logo: "/intelligence/logo.svg",
      logoWhite: "/intelligence/logo-white.svg",
    },
    openGraph: {
      title: "Intelligence Platform",
      description:
        "Intelligence Platform — campaign insights for modern brands.",
      image: "/intelligence/og.png",
    },
  },
};

const DEFAULT_TENANT = TENANT_CONFIG.beapolo;

export const resolveTenantKey = (hostname: string): TenantKey => {
  // En local (`npm start`) se ve con la marca de Intelligence.
  if (process.env.NODE_ENV === "development") return "intelligence";
  const normalizedHost = hostname.toLowerCase();
  const match = Object.values(TENANT_CONFIG).find((tenant) =>
    tenant.hostnameIncludes?.some((fragment) =>
      normalizedHost.includes(fragment.toLowerCase())
    )
  );
  return match?.key ?? DEFAULT_TENANT.key;
};

export const getTenantBranding = (hostname: string): TenantBranding => {
  const key = resolveTenantKey(hostname);
  return TENANT_CONFIG[key];
};

export const useTenantBranding = (): TenantBranding => {
  return useMemo(() => getTenantBranding(window.location.hostname), []);
};

export const applyBrandingToDocument = (branding: TenantBranding): void => {
  if (typeof document === "undefined") {
    return;
  }

  if (branding.title) {
    document.title = branding.title;
  }

  const faviconLink: HTMLLinkElement =
    (document.querySelector("link[rel='icon']") as HTMLLinkElement) ||
    document.createElement("link");

  faviconLink.rel = "icon";
  faviconLink.href = branding.assets.favicon;

  if (!faviconLink.parentElement) {
    document.head.appendChild(faviconLink);
  }

  const ensureMetaTag = (
    identifier: string,
    value: string,
    attribute: "name" | "property" = "name"
  ): void => {
    if (!value) {
      return;
    }
    const selector = `meta[${attribute}='${identifier}']`;
    let metaTag = document.querySelector(selector) as HTMLMetaElement | null;
    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.setAttribute(attribute, identifier);
      document.head.appendChild(metaTag);
    }
    metaTag.content = value;
  };

  const ogConfig = branding.openGraph ?? {};
  ensureMetaTag(
    "description",
    ogConfig.description ?? `${branding.displayName} platform`
  );
  ensureMetaTag("og:title", ogConfig.title ?? branding.title, "property");
  ensureMetaTag(
    "og:description",
    ogConfig.description ?? `${branding.displayName} platform`,
    "property"
  );

  // Prefer explicit OG asset, fallback to main logo for previews.
  if (typeof window !== "undefined") {
    const resolvedImage = ogConfig.image
      ? new URL(ogConfig.image, window.location.origin).toString()
      : new URL(branding.assets.logo, window.location.origin).toString();
    ensureMetaTag("og:image", resolvedImage, "property");
  }
};

export const getTenantLogo = (
  branding: TenantBranding,
  variant: "default" | "white" = "default"
): string => {
  if (variant === "white") {
    return branding.assets.logoWhite ?? branding.assets.logo;
  }
  return branding.assets.logo;
};
